import * as React from "react"

import { cn } from "@/lib/utils"
import { Networks } from "@/shadcn/components/ui/networks"
import { type ConnectedSource, type SourceNetwork } from "./types"

/**
 * The source-side marks: brand glyphs, monograms and the source chip.
 *
 * Separate from `<Networks>` because a SOURCE is not the same set as an output
 * network — these read from prod's svg-logos/ artwork (the raw marks, no badge
 * disc) rather than the network registry, and two sources have no entry there
 * at all.
 */

/**
 * prod: /dist/img/svg-logos/<file> — the raw source marks. Filenames must match
 * prod exactly (public/images/svg-logos holds byte copies), so swapping
 * `assetBaseUrl` to the CDN resolves without a rename step.
 *
 * PREFER THE `-color` VARIANT wherever prod ships one. svg-logos/ holds two
 * generations of each mark: the unsuffixed file is a desaturated tint
 * (youtube.svg is #ff8783, etsy.svg #e1a887, feed.svg #e4b19c) and the
 * `-color` file is the real brand colour (#f60231, #f56401, #e07d69). The tints
 * read as disabled artwork next to the outputs' own full-colour marks.
 *
 * Facebook is the one exception: it has no `-color` variant, because
 * facebook.svg is already brand blue (#257CE1).
 *
 * TikTok is absent: it ships only as a `dlvr-socials` font glyph, so
 * SOURCE_ICON_FONT covers it instead.
 */
const SOURCE_ICON: Record<SourceNetwork, string | null> = {
  tiktok: null, // font glyph only — see SOURCE_ICON_FONT
  rss: "feed-color.svg",
  instagram: "instagram-color-icon.svg",
  facebook: "facebook.svg", // no -color variant; already brand blue
  youtube: "youtube-color.svg",
  pinterest: "pinterest-color.svg",
  wordpress: "wordpress-color.svg",
  etsy: "etsy-color.svg",
  shopify: "shopify-color.svg",
  substack: "substack-color.svg",
}

/**
 * Sources with no SVG in svg-logos/ — rendered from the `dlvr-socials` icon
 * font. Monochrome: they take `currentColor`.
 */
const SOURCE_ICON_FONT: Partial<Record<SourceNetwork, string>> = {
  tiktok: "icon-tiktok",
}

/**
 * Optical size correction for a mark whose ink is much wider than it is tall,
 * applied to the image rather than its box so layout doesn't shift.
 *
 * Empty since the `-color` swap: it existed for `etsy.svg`, a ~498×185 wordmark
 * that `object-contain` fitted by width at ~37% of its siblings' height, and
 * `etsy-color.svg` is the square badge that entry was waiting for. Correct a new
 * mark here only past roughly 1.4:1 — youtube-color.svg is 487×369 and left
 * alone, since scaling a wide glyph to fill the height overflows sideways.
 */
const SOURCE_MARK_SCALE: Partial<Record<SourceNetwork, string>> = {}

export const SOURCE_LABEL: Record<SourceNetwork, string> = {
  rss: "RSS Feed",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  pinterest: "Pinterest",
  wordpress: "WordPress",
  etsy: "Etsy",
  shopify: "Shopify",
  substack: "Substack",
}

export function SourceMark({
  network,
  base,
  size = "tw:size-11",
  fontSize = "tw:text-[40px]",
}: {
  network: SourceNetwork
  base: string
  size?: string
  /** Glyph size for icon-font sources, which scale by font-size not box. */
  fontSize?: string
}) {
  const iconClass = SOURCE_ICON_FONT[network]
  if (iconClass) {
    return (
      <span
        className={cn(
          "tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:text-foreground",
          size
        )}
        role="img"
        aria-label={SOURCE_LABEL[network]}
      >
        <i className={cn("ds-icon", iconClass, fontSize)} aria-hidden />
      </span>
    )
  }
  const file = SOURCE_ICON[network]
  if (!file) return null
  const scale = SOURCE_MARK_SCALE[network]
  return (
    <span
      className={cn(
        "tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center",
        // A scaled mark paints past its box on purpose — clipping would undo it.
        scale ? "tw:overflow-visible" : "tw:overflow-hidden",
        size
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={`${base}/svg-logos/${file}`}
        alt={SOURCE_LABEL[network]}
        className={cn("tw:size-full tw:object-contain", scale)}
      />
    </span>
  )
}

/** Square brand-coloured monogram used by feed rows, chips and previews. */
export function Monogram({
  letter,
  color,
  className,
  textClassName = "tw:text-lg",
}: {
  letter: string
  color?: string
  className?: string
  textClassName?: string
}) {
  return (
    <span
      className={cn(
        "tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md tw:font-semibold tw:text-white",
        !color && "tw:bg-muted-foreground",
        textClassName,
        className
      )}
      // Brand colour is backend data, not a theme decision.
      style={color ? { backgroundColor: color } : undefined}
      aria-hidden
    >
      {letter}
    </span>
  )
}

/**
 * The source's own picture where it has one, its network mark or monogram
 * otherwise.
 *
 * `iconUrl` wins over the network mark: for a feed it is the publisher's
 * favicon and for a social input it is the account's profile picture, and either
 * identifies THIS source where the network mark only says what kind it is. The
 * name sits beside it, so nothing is lost by dropping the mark.
 *
 * Falls back on error, not just on absence. A feed icon is whatever the
 * publisher serves and an avatar is a network CDN link that expires, so a dead
 * or hotlink-blocked URL is routine rather than exceptional — FindStep's rows
 * have always done this, and a chip showing a broken-image glyph is worse than
 * one showing the mark.
 */
export function SourceAvatar({
  source,
  base,
  size = "tw:size-[22px]",
  radius = "tw:rounded-sm",
  textClassName = "tw:text-xs",
  px = 22,
}: {
  source: ConnectedSource
  base: string
  size?: string
  radius?: string
  textClassName?: string
  /** Pixel edge for the network mark, which sizes by prop rather than class. */
  px?: number
}) {
  const [failed, setFailed] = React.useState(false)

  // Reset when the picture changes. The chip keeps its position across steps, so
  // React reuses this instance as the flow moves from one source to the next —
  // without this, one 404'd favicon would make every later source in the slot
  // fall back to its monogram for the rest of the session.
  React.useEffect(() => setFailed(false), [source.iconUrl])

  if (source.iconUrl && !failed) {
    return (
      /* eslint-disable-next-line @next/next/no-img-element */
      <img
        src={source.iconUrl}
        alt=""
        onError={() => setFailed(true)}
        /*
         * No `bg-muted` here. With `object-cover` the image fills the box, so
         * the only time the backdrop showed was through a transparent one — a
         * network logo — where it read as a grey disc behind the mark. The
         * monogram branch below still carries its own background.
         */
        className={cn("tw:shrink-0 tw:object-cover", size, radius)}
      />
    )
  }

  if (source.network) {
    return (
      <Networks
        name={source.network}
        variant="color"
        size={px}
        assetBaseUrl={base}
      />
    )
  }

  return (
    <Monogram
      letter={source.monogram}
      color={source.monogramColor}
      className={cn(size, radius)}
      textClassName={textClassName}
    />
  )
}

/** "N New Scientist · ~5 new/week" — recurs on steps 1, 2 and 3. */
export function SourceChip({
  source,
  base,
  className,
  size = "sm",
}: {
  source: ConnectedSource
  base: string
  className?: string
  /**
   * "md" is the summary's chip. The review card is the last thing read before
   * committing, and at the default size the source it names sat smaller than the
   * fields under it; everywhere else the chip labels a step already in progress
   * and should stay out of the way.
   */
  size?: "sm" | "md"
}) {
  // Hugs its content, but grows to the full width rather than wrapping: account
  // names are user data in prod, and without `max-w-full` + a truncating name
  // the chip became a two-line pill with the mark orphaned on the first line.
  return (
    <div
      className={cn(
        "tw:inline-flex tw:w-fit tw:max-w-full tw:items-center tw:rounded-md tw:border tw:border-border",
        size === "md"
          ? "tw:gap-2.5 tw:py-2 tw:pr-3.5 tw:pl-2.5"
          : "tw:gap-2 tw:py-1.5 tw:pr-3 tw:pl-2",
        className
      )}
    >
      <SourceAvatar
        source={source}
        base={base}
        {...(size === "md"
          ? { size: "tw:size-7", radius: "tw:rounded-md", px: 28 }
          : {})}
      />
      <span
        className={cn(
          "tw:min-w-0 tw:truncate tw:font-medium tw:text-foreground",
          size === "md" ? "tw:text-base" : "tw:text-sm"
        )}
      >
        {source.name}
      </span>
      {source.velocity ? (
        <span
          className={cn(
            "tw:shrink-0 tw:whitespace-nowrap tw:text-muted-foreground",
            size === "md" ? "tw:text-base" : "tw:text-sm"
          )}
        >
          · {source.velocity}
        </span>
      ) : null}
    </div>
  )
}
